import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Holding } from '../types';
import { adminApi } from '../services/api';

interface Props {
  holdings?: Holding[];
}

export const MultiBrokerVaultManager: React.FC<Props> = ({ holdings = [] }) => {
  const { language } = useLanguage();
  const [activeBroker, setActiveBroker] = useState<string>('all');
  const [cashBalance, setCashBalance] = useState<number>(3677.64);

  useEffect(() => {
    adminApi.getCashBalances().then(res => {
      if (res.data?.balances?.EUR !== undefined) {
        setCashBalance(Number(res.data.balances.EUR));
      }
    }).catch(err => console.error("VaultManager: failed to fetch cash", err));
  }, []);

  // Compute live value of CSG holdings in Eurobank
  const csgHolding = holdings.find(h => h.symbol === 'CSG');
  const csgShares = csgHolding ? csgHolding.quantity : 12000;
  const csgPrice = csgHolding ? (csgHolding.current_price || csgHolding.avg_cost || 14.654) : 14.654;
  const csgMarketVal = csgShares * csgPrice;
  const eurobankTotal = csgMarketVal + cashBalance;

  const brokers = [
    { 
      id: 'all', 
      name: language === 'sk' ? 'Celkové Portfólio (Konsolidované)' : 'Consolidated Portfolio', 
      balance: eurobankTotal, 
      badge: 'CONSOLIDATED', 
      sub: `${csgShares.toLocaleString()} ks CSG NV + Cash`
    },
    { 
      id: 'eurobank', 
      name: 'Eurobank Cyprus (Account 113793-1)', 
      balance: eurobankTotal, 
      badge: 'EUROBANK WM', 
      sub: 'Wealth Management • Euronext Amsterdam'
    },
    { 
      id: 'cash', 
      name: language === 'sk' ? 'Hotovosť (Investičný účet EUR)' : 'Eurobank EUR Cash Account', 
      balance: cashBalance, 
      badge: 'CASH LIQUIDITY', 
      sub: 'Voľná hotovosť k investovaniu'
    }
  ];

  return (
    <div className="glass-panel p-6 space-y-4 font-mono">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-900 flex items-center gap-2">
            <span>🏢</span>
            {language === 'sk' ? 'Správa Bankových a Broker Účtov (Eurobank Wealth Management)' : 'Multi-Account / Broker Vault (Eurobank Wealth Management)'}
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {language === 'sk' 
              ? 'Oficiálny účet Anogi Enterprises Limited č. 113793-1 v Eurobank Cyprus • CSG NV (NL0015073TS8)' 
              : 'Official Anogi Enterprises Limited Account #113793-1 at Eurobank Cyprus • CSG NV (NL0015073TS8)'}
          </p>
        </div>

        <button
          onClick={() => alert(language === 'sk' ? '➕ Nové prepojenie: Synchronizácia prebieha automaticky z invest@anogilimited.com.' : '➕ Automated sync active from invest@anogilimited.com.')}
          className="px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-900 text-white hover:opacity-90 transition-all flex items-center gap-1.5 shadow-sm"
        >
          <span>🔄</span>
          <span>{language === 'sk' ? 'Stav Synchronizácie' : 'Sync Active'}</span>
        </button>
      </div>

      {/* Broker Selector Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {brokers.map((b) => {
          const isSelected = activeBroker === b.id;
          return (
            <button
              key={b.id}
              onClick={() => setActiveBroker(b.id)}
              className={`p-3.5 rounded-2xl border text-left transition-all flex flex-col justify-between h-24 ${
                isSelected 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-md scale-[1.01]' 
                  : 'bg-white text-slate-800 border-slate-200 hover:border-slate-400'
              }`}
            >
              <div className="flex justify-between items-start w-full">
                <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  isSelected ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
                }`}>{b.badge}</span>
                {isSelected && <span className="text-xs text-emerald-400 font-bold">✓ Active</span>}
              </div>

              <div>
                <div className="text-[10px] font-bold truncate opacity-90">{b.name}</div>
                <div className="text-sm font-extrabold mt-0.5">
                  €{b.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

